import Bugsnag from '@bugsnag/browser';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DirectLink } from '@src/core/buyerApiClient';
import { RootState, AppDispatch, getClient, bearerAuth } from '@src/core/store';
import { setupCart } from '@src/features/cart/store/cart';
import { resetSale, fetchSaleRequiredFormInputs, setCurrentSale, setProcessedSale, loadSaleAsCurrent } from './sale';
import { clearBuyer } from '@src/features/customers';
import { fetchOrderRequiredFormInputs, resetOrder, setCurrentOrder } from '@src/features/orders/store/order';

export interface DirectLinkState {
  directLink?: DirectLink;
  error?: string;
  status: 'idle' | 'loading' | 'loaded' | 'error' | 'finished';
}

const initialState: DirectLinkState = {
  directLink: undefined,
  error: undefined,
  status: 'idle',
};

// Reducers

export const directLinkSlice = createSlice({
  name: 'directLink',
  initialState,
  reducers: {
    setDirectLink: (state: DirectLinkState, action: PayloadAction<DirectLink | undefined>) => {
      state.directLink = action.payload;
    },
    setProcessedDirectLink: (state: DirectLinkState, action: PayloadAction<DirectLink, string>) => {
      state.directLink = action.payload;
    },
    setError: (state: DirectLinkState, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setStatus: (state: DirectLinkState, action: PayloadAction<DirectLinkState['status']>) => {
      state.status = action.payload;
    },
    resetDirectLink: () => initialState,
  },
});

// Selectors

export const directLinkStateSelector = (state: RootState) => state.directLink;

// Actions

export const setupDirectLink = () => async (dispatch: AppDispatch) => {
  dispatch(resetSale());
  dispatch(resetOrder());
  dispatch(resetDirectLink());
  dispatch(clearBuyer());
};

/**
 * Load Sale with or without vouchers depending on authentication.
 * If logged in, fetch Sale with vouchers. Otherwise, fetch Sale without authentication (public endpoint).
 */
export const loadSaleForDirectLink = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  saleId: string,
  setter: typeof setCurrentSale | typeof setProcessedSale,
) => {
  const state = getState();
  const { buyerToken } = state.auth;

  if (buyerToken) {
    // If logged in, fetch Sale with vouchers
    return loadSaleAsCurrent(dispatch, getState, saleId, setter);
  }

  // If not logged in, fetch Sale without authentication (no vouchers)
  const { data: sale } = await getClient(state).sales.getSale(saleId);
  dispatch(setter(sale, []));

  return sale;
};

/**
 * The Order is public in the Buyer API, so it is always fetched without authentication.
 * A link Order is still unpaid, and vouchers only exist after payment, so none are fetched.
 */
export const loadOrderForDirectLink = async (dispatch: AppDispatch, getState: () => RootState, orderId: string) => {
  const { data: order } = await getClient(getState()).orders.getOrder(orderId);

  dispatch(setCurrentOrder(order, []));

  return order;
};

// Bumped by every load: the widget reloads on a code change, and a slower load of the previous code
// must not land its link, sale or order over the one the buyer has open now.
let latestDirectLinkLoad = 0;

export const loadDirectLink = (directLinkCode: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const load = ++latestDirectLinkLoad;
  const dispatchWhileCurrent = ((action: Parameters<AppDispatch>[0]) =>
    load === latestDirectLinkLoad ? dispatch(action) : undefined) as AppDispatch;

  dispatch(setStatus('loading'));
  // The widget reloads on a code change without setupDirectLink; the checkout would pay a leftover sale or order.
  dispatch(resetSale());
  dispatch(resetOrder());

  try {
    const { data: link } = await getClient(getState()).directLinks.getDirectLink(directLinkCode);
    dispatchWhileCurrent(setDirectLink(link));

    if (link.type === 'cart') {
      await dispatchWhileCurrent(setupCart(directLinkCode));
    }

    if (link.type === 'sale' && link.saleId) {
      await loadSaleForDirectLink(dispatchWhileCurrent, getState, link.saleId, setCurrentSale);
    }

    if (link.type === 'order') {
      if (!link.orderId) {
        Bugsnag.notify(
          { name: 'OrderDirectLinkWithoutOrderId', message: 'Order direct link has no orderId' },
          (event) => event.addMetadata('directLink', { id: link.id, code: directLinkCode }),
        );
        dispatchWhileCurrent(
          setError('Este pedido não está mais disponível. Por favor, entre em contato com quem enviou o link.'),
        );
        dispatchWhileCurrent(setStatus('error'));
        return;
      }

      await loadOrderForDirectLink(dispatchWhileCurrent, getState, link.orderId);
    }

    dispatchWhileCurrent(setStatus('loaded'));
  } catch (error) {
    dispatchWhileCurrent(
      setError('Página não encontrada. Por favor, confira o link enviado para você e tente novamente.'),
    );

    dispatchWhileCurrent(setStatus('error'));
  }
};

/**
 * The link code is the only access the buyer needs: the Buyer API skips ownership on the sale and order
 * checkout routes, and the link customer is rarely the logged buyer.
 */
export const setupDirectLinkCheckout = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const { directLink } = directLinkStateSelector(state);
  const { buyerToken } = state.auth;

  if (!buyerToken || !directLink) {
    return;
  }

  // loadDirectLink usually stored the transactable already; the inputs step reloads it with the buyer token.
  if (directLink.type === 'order' && directLink.orderId) {
    const { orderId } = directLink;
    const { currentOrder } = state.order;
    const order =
      currentOrder?.id === orderId ? currentOrder : await loadOrderForDirectLink(dispatch, getState, orderId);

    await dispatch(fetchOrderRequiredFormInputs(order));
  }

  if (directLink.type === 'sale' && directLink.saleId) {
    const { saleId } = directLink;
    let sale = state.sale.currentSale;

    if (sale?.id !== saleId) {
      sale = (await getClient(state).sales.getSale(saleId, bearerAuth(buyerToken))).data;
      dispatch(setCurrentSale(sale, []));
    }

    await dispatch(fetchSaleRequiredFormInputs(sale));
  }
};

export const { setDirectLink, setProcessedDirectLink, setStatus, setError, resetDirectLink } = directLinkSlice.actions;

export default directLinkSlice.reducer;
